import { KnockFeedProvider, KnockProvider } from '@knocklabs/react';
import '@knocklabs/react/dist/index.css';
import { Component, createContext, type ErrorInfo, type ReactNode, useContext } from 'react';

import { useAuth } from 'src/auth';

const KnockReadyContext = createContext(false);

export const useKnockReady = () => useContext(KnockReadyContext);

interface KnockNotificationProviderProps {
  children: ReactNode;
}

class KnockErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Knock] Provider error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return <KnockReadyContext.Provider value={false}>{this.props.children}</KnockReadyContext.Provider>;
    }
    return this.props.children;
  }
}

/**
 * Wraps children with Knock notification providers when the user is authenticated.
 * Requires KNOCK_PUBLIC_API_KEY and KNOCK_FEED_CHANNEL_ID env vars.
 */
export function KnockNotificationProvider({ children }: KnockNotificationProviderProps) {
  const { currentUser } = useAuth();

  const apiKey = process.env.KNOCK_PUBLIC_API_KEY;
  const feedId = process.env.KNOCK_FEED_CHANNEL_ID;

  // Don't render Knock providers if missing config or unauthenticated user
  if (!apiKey || !feedId || !currentUser?.knockId) {
    if (currentUser) {
      console.debug(
        '[Knock] Not ready — knockId:',
        currentUser.knockId ?? 'missing',
        '| apiKey:',
        !!apiKey,
        '| feedId:',
        !!feedId
      );
    }
    return <KnockReadyContext.Provider value={false}>{children}</KnockReadyContext.Provider>;
  }

  return (
    <KnockErrorBoundary>
      <KnockReadyContext.Provider value={true}>
        <KnockProvider apiKey={apiKey} userId={currentUser.knockId}>
          <KnockFeedProvider
            feedId={feedId}
            // 'rich' mode required — Knock's default 'compact' strips nested data and drops linked_record (TAS-3173).
            defaultFeedOptions={{
              mode: 'rich',
              tenant: currentUser.organizationId ? String(currentUser.organizationId) : undefined,
            }}
          >
            {children}
          </KnockFeedProvider>
        </KnockProvider>
      </KnockReadyContext.Provider>
    </KnockErrorBoundary>
  );
}
